<template>
  <CCard class="mt-3">
    <CCardHeader>
      <CRow class="d-flex align-items-center pl-3">
        <h5 class="text-primary mb-0">
          <i class="fa-solid fa-chalkboard-user mr-2" />
          Course Progress
        </h5>
      </CRow>
    </CCardHeader>

    <CCardBody>
      <div v-if="loading" class="text-center py-4">
        <CSpinner color="primary" />
      </div>

      <div v-else>

        <div class="d-flex px-2 mb-1"
          style="font-size:11px; font-weight:600; color:#888; text-transform:uppercase; letter-spacing:0.05em;">
          <div style="width:30px;"></div>
          <div style="flex:3;">Course</div>
          <div style="flex:1; text-align:center;">Enrolled</div>
          <div style="flex:2; padding:0 12px;">Avg Progress</div>
          <div style="flex:1; text-align:right; padding-right:4px;">Manage</div>
        </div>

        <div v-if="courses.length === 0" class="text-center text-muted py-3">
          No Moodle courses found
        </div>

        <div v-for="course in courses" :key="course.training_course_id" class="mb-1">

          <div class="d-flex align-items-center p-2 rounded" :style="expandedCourse === course.training_course_id
            ? 'background: #f0f4ff; border: 1px solid #d0d8f0; cursor:pointer;'
            : 'background: #fafafa; border: 1px solid #e8e8e8; cursor:pointer;'" @click="toggleCourse(course)">
            <div style="width:30px; text-align:center; flex-shrink:0;">
              <i :class="expandedCourse === course.training_course_id
                ? 'fas fa-chevron-down text-primary'
                : 'fas fa-chevron-right text-muted'" style="font-size:11px;" />
            </div>

            <div style="flex: 3; font-weight:500; font-size:13px;">
              {{ course.course_title || '—' }}
            </div>

            <div style="flex:1; text-align:center;">
              <CBadge color="info">{{ course.enrolled_count }}</CBadge>
            </div>

            <div style="flex:2; padding: 0 12px;">
              <CProgress :value="course.avg_progress" size="sm"
                :color="course.avg_progress >= 100 ? 'success' : course.avg_progress >= 50 ? 'warning' : 'danger'" />
              <small class="text-muted">{{ course.avg_progress }}% avg</small>
            </div>

            <div style="flex:1; text-align:right; padding-right:4px;" @click.stop>
              <CButton color="secondary" size="sm" variant="outline" @click.stop="manageCourse(course)">
                <i class="fas fa-external-link-alt mr-1" style="font-size:11px" />
                Moodle
              </CButton>
            </div>
          </div>

          <div v-if="expandedCourse === course.training_course_id"
            class="border-left border-right border-bottom rounded-bottom p-2"
            style="background:#f8f9ff; border-color:#d0d8f0 !important;">
            <div v-if="loadingCandidates" class="text-center py-3">
              <CSpinner size="sm" color="primary" />
            </div>

            <div v-else-if="candidates.length === 0" class="text-center py-2 text-muted" style="font-size:13px;">
              No candidates enrolled
            </div>

            <div v-else>
              <div class="d-flex px-2 pb-1"
                style="font-size:11px; font-weight:600; color:#888; text-transform:uppercase; letter-spacing:0.05em;">
                <div style="width:30px;"></div>
                <div style="flex:3;">Candidate Name</div>
                <div style="flex:3;">Email</div>
                <div style="flex:2;">Progress</div>
                <div style="flex:1;">Status</div>
                <div style="flex:2;">Last Updated</div>
              </div>

              <div v-for="c in candidates" :key="c.candidate_uid"
                class="d-flex align-items-center px-2 py-1 rounded mb-1"
                style="background:#fff; border: 1px solid #e8ecf8; font-size:13px;">
                <div style="width:30px; text-align:center;">
                  <i class="fas fa-user-circle" style="color:#aaa; font-size:14px;" />
                </div>
                <div style="flex:3;">{{ c.full_name  }}</div>
                <div style="flex:3;">{{ c.email }}</div>
                <div style="flex:2; padding-right:12px;">
                  <CProgress :value="c.overall_progress" size="sm"
                    :color="c.overall_progress >= 100 ? 'success' : c.overall_progress > 0 ? 'warning' : 'danger'" />
                  <small class="text-muted">{{ c.overall_progress }}%</small>
                </div>
                <div style="flex:1;">
                  <CBadge :color="statusColor(c.status)">{{ statusLabel(c.status) }}</CBadge>
                </div>
                <div style="flex:2;">
                  <small class="text-muted">
                    {{ c.progress_updated_at ? formatDate(c.progress_updated_at) : '—' }}
                  </small>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import course from "@/services/course"
import m from "moment"

export default {
  name: "MoodleCourseDashboard",

  data() {
    return {
      loading: false,
      loadingCandidates: false,
      courses: [],
      candidates: [],
      expandedCourse: null,
    }
  },

  async mounted() {
    await this.loadCourses()
  },

  methods: {
    async loadCourses() {
      this.loading = true
      try {
        const res = await course.getAdminCoursesDashboard()
        this.courses = res.data
      } catch (err) {
        console.error("Failed to load courses", err)
      } finally {
        this.loading = false
      }
    },

    async toggleCourse(courseItem) {
      if (this.expandedCourse === courseItem.training_course_id) {
        this.expandedCourse = null
        this.candidates = []
        return
      }
      this.expandedCourse = courseItem.training_course_id
      this.candidates = []
      if (courseItem.enrolled_count === 0) return
      this.loadingCandidates = true
      try {
        const res = await course.getCoursesCandidatesProgress(courseItem.training_course_id)
        this.candidates = res.data.candidates
      } catch (err) {
        console.error("Failed to load candidates", err)
      } finally {
        this.loadingCandidates = false
      }
    },

    async manageCourse(courseItem) {
      try {
        const res = await course.getMoodleManageUrl(courseItem.training_course_id)
        if (res.data?.sso_url) {
          const moodleWindow = window.open(res.data.sso_url, '_blank')
          this.pollProgressAfterMoodle(courseItem, moodleWindow)
        }
      } catch (err) {
        console.error("Failed to get manage URL", err)
      }
    },

    pollProgressAfterMoodle(courseItem, moodleWindow) {
      const interval = setInterval(async () => {
        if (moodleWindow && moodleWindow.closed) {
          clearInterval(interval)
          try {
            const res = await course.syncCourseProgress(courseItem.training_course_id)
            const updated = res.data?.overall_pct ?? 0
            const idx = this.courses.findIndex(c => c.training_course_id === courseItem.training_course_id)
            if (idx !== -1 && updated > 0) {
              this.$set(this.courses, idx, { ...this.courses[idx], avg_progress: updated })
            }
          } catch (err) {
            console.error("Failed to sync progress after Moodle", err)
          }
        }
      }, 5000)
    },

    statusColor(s) {
      return s === "completed" ? "success" : s === "in_progress" ? "warning" : "danger"
    },

    statusLabel(s) {
      return s === "completed" ? "Completed" : s === "in_progress" ? "In Progress" : "Not Started"
    },

    formatDate(d) {
      return m(d).format("DD MMM YYYY HH:mm")
    },
  },
}
</script>